import { createCustomFilterForEntity } from "@shared/backend/create-custom-filter-for-entity";
import {
  buildFutureOpenTabSignalsSql,
  buildUntreatedTabSignalsSql,
  type OpportunitiesTableTabSqlRowScope,
} from "@shared/helpers/opportunity-admin-tab-sql";
import { opportunityHasNoCoordinatesSql } from "@shared/helpers/geo";
import { opportunityGroupParentSeatsFullSql } from "@shared/helpers/opportunity-occupancy";
import {
  opportunityDailyTargetReachedSql,
  opportunityHasDailyVolunteerTargetSql,
} from "@shared/helpers/daily-volunteer-target";
import {
  filterWithRelationsAndJson,
  type RelationalOrJsonEntityFilter,
} from "@shared/helpers/sql-relations";
import { dbNamesOf, remult, repo, SqlDatabase } from "remult";
import type { Team } from "../team";
import { User } from "../user";
import { Opportunity } from "./";

const userRepo = repo(User);

const createCustomFilter = createCustomFilterForEntity<Opportunity>();

export class OpportunityFilters {
  /**
   * # 🌟✨ HyperFilter – The Ultimate Filtering Powerhouse!
   * ### 🤝 Filter through complex relations effortlessly
   * ### 📦 Directly filter JSON fields (no backend work required!)
   * ### 🚀 Transforms your filter into a single, optimized DB query
   * ### 🔒 Engineered for maximum SQL injection protection
   *
   * ---
   * ### Example:
   * ```ts
   * myEntityRepo.find({
   *  where: MyEntity.hyperFilter({ ... })
   * })
   * ```
   *
   * ---
   * Built by Natan Salmon
   */
  static hyperFilter = createCustomFilter<
    RelationalOrJsonEntityFilter<Opportunity>
  >((filters) => {
    return filterWithRelationsAndJson(Opportunity)(filters);
  });

  static get dbNames() {
    return dbNamesOf(Opportunity, {
      tableName: true,
    });
  }

  static isLocationInRadiusFromPoint = createCustomFilter<{
    lat: number;
    lng: number;
    radius: {
      distance: number;
      measure: "km" | "m";
    };
  }>((coordinates) => {
    return SqlDatabase.rawFilter(async (build) => {
      const {
        lat,
        lng,
        radius: { distance, measure },
      } = coordinates;

      const dbNames = await this.dbNames;
      const radiusMeters = measure === "km" ? distance * 1000 : distance;

      build.sql = `
      (
        (${build.param(lat)}::float = 0 AND ${build.param(lng)}::float = 0)
        OR ST_DWithin(
          ${dbNames.coordinatesGeog},
          ST_SetSRID(ST_MakePoint(${build.param(lng)}, ${build.param(lat)}), 4326)::geography,
          ${build.param(radiusMeters)}
        )
        )
      `;
    });
  });

  static doesAgeSatisfyRequiredAge = createCustomFilter(async () => {
    const user = await userRepo.findId(remult.user?.id || "111");
    const birthDate = new Date(user?.dateOfBirth || "");

    // Check if birthDate is valid
    if (isNaN(birthDate.getTime())) {
      // Handle the case where the birthDate is invalid
      return SqlDatabase.rawFilter((whereFragment) => {
        // Returning an impossible condition or empty filter to avoid errors
        whereFragment.sql = "1 = 1";
      });
    }

    // Current date
    const currentDate = new Date();

    // Calculate the difference in years
    let age = currentDate.getFullYear() - birthDate.getFullYear();

    // Adjust the age if the date of birth hasn't occurred yet this year
    if (
      currentDate.getMonth() < birthDate.getMonth() ||
      (currentDate.getMonth() === birthDate.getMonth() &&
        currentDate.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    // Ensure age is a valid number
    if (isNaN(age)) {
      return SqlDatabase.rawFilter((whereFragment) => {
        whereFragment.sql = "1 = 1";
      });
    }

    const opportunity = await this.dbNames;

    return SqlDatabase.rawFilter((build) => {
      const ageParam = build.param(age);
      build.sql = `${ageParam} >= ${opportunity.minAge} AND ${ageParam} <= ${opportunity.maxAge}`;
    });
  });

  static isInNeedOfParticipants = createCustomFilter<{ spaces?: number }>(
    ({ spaces = 1 }) => {
      return SqlDatabase.rawFilter(async (build) => {
        const opportunity = await this.dbNames;

        const metadata = repo(Opportunity).metadata;

        const participantCountSqlExpression =
          metadata.fields.participantsCount.options.sqlExpression;

        const participantCount =
          typeof participantCountSqlExpression === "string"
            ? participantCountSqlExpression
            : await participantCountSqlExpression!(metadata);

        build.sql = `(${participantCount} <= (${opportunity.amount} - ${build.param(spaces)}) AND ${opportunity.isFull} = false)`;
      });
    },
  );

  static dailyVolunteerTargetState = createCustomFilter<{
    state: "has" | "reached" | "open";
  }>(({ state }) => {
    return SqlDatabase.rawFilter(async (build) => {
      const hasTarget = opportunityHasDailyVolunteerTargetSql();
      const reached = opportunityDailyTargetReachedSql();
      if (state === "has") build.sql = hasTarget;
      else if (state === "reached") build.sql = reached;
      else build.sql = `(${hasTarget} AND NOT ${reached})`;
    });
  });

  static isFull = createCustomFilter(() => {
    return {
      $or: [
        { $not: this.isInNeedOfParticipants({ spaces: 1 }) },
        SqlDatabase.rawFilter((build) => {
          build.sql = opportunityGroupParentSeatsFullSql("opportunity");
        }),
      ],
    };
  });

  /** מענה חסר: last volunteer-registration day is still on or after dateFrom. */
  static registrationDeadlineOnOrAfter = createCustomFilter<{
    dateFrom: string;
  }>(({ dateFrom }) => {
    return SqlDatabase.rawFilter((build) => {
      const from = build.param(dateFrom);
      build.sql = `(("opportunity"."last_call_to_volunteers" AT TIME ZONE 'Asia/Jerusalem')::date >= ${from}::date)`;
    });
  });

  /**
   * Admin table: filter by remaining free seats, GREATEST(amount - approved participants, 0).
   */
  static freeSeatsInRange = createCustomFilter<{
    min?: number;
    max?: number;
  }>(({ min, max }) => {
    return SqlDatabase.rawFilter(async (build) => {
      const opportunity = await this.dbNames;
      const metadata = repo(Opportunity).metadata;
      const participantCountSqlExpression =
        metadata.fields.participantsCount.options.sqlExpression;
      const participantCount =
        typeof participantCountSqlExpression === "string"
          ? participantCountSqlExpression
          : await participantCountSqlExpression!(metadata);

      const freeSeats = `GREATEST((${opportunity.amount} - (${participantCount})), 0)`;
      const parts: string[] = [];
      if (min != null && Number.isFinite(min)) {
        parts.push(`${freeSeats} >= ${build.param(min)}`);
      }
      if (max != null && Number.isFinite(max)) {
        parts.push(`${freeSeats} <= ${build.param(max)}`);
      }
      build.sql = parts.length > 0 ? `(${parts.join(" AND ")})` : "1 = 1";
    });
  });

  /**
   * "פתוחות" / "לא טופלו" tabs: group parents match on having a live child.
   * Other rows match on the isFull flag, without counting occupied seats.
   */
  static opportunitiesTableOpenTabsEligibility = createCustomFilter(() => ({
    $or: [
      this.hyperFilter({
        parentOpportunityId: null,
        childOpportunities: { $some: { isDeleted: false } },
      }),
      { isFull: false },
    ],
  }));

  /**
   * Admin opportunities table "פתוחות" / "לא טופלו" tabs.
   * Group parents are classified from child instances (not synced parent dates alone).
   */
  static matchesOpportunitiesTableTabSignals = createCustomFilter<{
    tab: "futureOpen" | "unHandled";
    now?: Date;
    rowScope?: OpportunitiesTableTabSqlRowScope;
  }>(({ tab, now = new Date(), rowScope = "roots" }) => {
    return SqlDatabase.rawFilter((build) => {
      const nowParam = build.param(now);
      build.sql =
        tab === "futureOpen"
          ? buildFutureOpenTabSignalsSql("opportunity", nowParam, rowScope)
          : buildUntreatedTabSignalsSql("opportunity", nowParam, rowScope);
    });
  });

  static isUserParticipant = createCustomFilter(async () => {
    return this.hyperFilter({
      participantUsers: {
        $some: {
          association: {
            $is: {
              userId: remult.user?.id,
            },
          },
        },
      },
    });
  });

  static isUserNotParticipant = createCustomFilter(async () => {
    return this.hyperFilter({
      participantUsers: {
        $none: {
          association: {
            $is: {
              userId: remult.user?.id,
            },
          },
        },
      },
    });
  });

  static isUserInOneOfTeams = createCustomFilter(async () => {
    return this.hyperFilter({
      teams: {
        $some: {
          team: {
            $is: {
              associations: {
                $some: {
                  association: {
                    $is: {
                      userId: remult.user?.id,
                    },
                  },
                },
              },
            },
          },
        },
      },
    });
  });

  static isUserInOrganization = createCustomFilter(async () => {
    return this.hyperFilter({
      organization: {
        $is: {
          associations: {
            $some: {
              userId: remult.user?.id,
            },
          },
        },
      },
    });
  });

  static isInOneOfTheseTeams = createCustomFilter<{ teams: string[] | Team[] }>(
    ({ teams }) =>
      this.hyperFilter({
        teams: {
          $some: {
            teamId: {
              $in: teams.map((t) => (typeof t === "string" ? t : t.id)),
            },
          },
        },
      }),
  );

  static isAddressInRadiusFromPoint = createCustomFilter<{
    lat: number;
    lng: number;
    radius: {
      distance: number;
      measure: "km" | "m";
    };
  }>((coordinates) => {
    return SqlDatabase.rawFilter(async (build) => {
      const {
        lat,
        lng,
        radius: { distance, measure },
      } = coordinates;

      const radiusMeters = measure === "km" ? distance * 1000 : distance;

      build.sql = `
        EXISTS (
          SELECT 1
          FROM "opportunity" o
          WHERE o.id = opportunity.id AND (
            (${build.param(lat)}::float = 0 AND ${build.param(lng)}::float = 0)
            OR ST_DWithin(
              o.coordinates_geog,
              ST_SetSRID(ST_MakePoint(${build.param(lng)}, ${build.param(lat)}), 4326)::geography,
              ${build.param(radiusMeters)}
            )
          )
        )
      `;
    });
  });

  static inCbsDistricts = createCustomFilter<{ codes: string[] }>(
    ({ codes }) => {
      return SqlDatabase.rawFilter(async (build) => {
        if (codes.length === 0) {
          build.sql = "false";
          return;
        }
        const codeList = codes.map((code) => build.param(code)).join(", ");
        build.sql = `
          EXISTS (
            SELECT 1
            FROM public."israel-cbs-boundary" icb
            WHERE icb.boundary_type = 'district'
              AND icb.code IN (${codeList})
              AND opportunity.coordinates_geog IS NOT NULL
              AND ST_Contains(icb.geometry, opportunity.coordinates_geog::geometry)
          )
        `;
      });
    },
  );

  /** Rows with no usable map coordinates (standalone, group parent, or child instance). */
  static hasNoCoordinates = createCustomFilter(() => {
    return SqlDatabase.rawFilter(async (build) => {
      const dbNames = await this.dbNames;
      build.sql = opportunityHasNoCoordinatesSql(
        dbNames.coordinatesGeog,
        dbNames.coordinates,
      );
    });
  });
}
